import { SearchOutlined } from '@ant-design/icons';
import { t } from '@framework/i18n';
import { Maybe } from '@framework/validation';
import type { CheckboxOptionType, TableColumnProps } from 'antd';
import { Button, Checkbox, Input, Radio, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import * as React from 'react';
import styled from 'styled-components';
import { DatePickerFilterStyle } from './DatePickerFilterStyle';
import { TableColumnFilterButtons } from './TableColumnFilterButtons';

const Container = styled.div`
  padding: 8px;
`;
const CheckboxGroupStyle = styled(Checkbox.Group)`
  width: 100%;
  display: block;
  margin-bottom: 8px;
`;

const RadioGroupStyle = styled(Radio.Group)`
  width: 100%;
  display: block;
  margin-bottom: 8px;
`;

const InputStyle = styled(Input)`
  width: 188px;
  display: block;
  margin-bottom: 8px;
`;
const ButtonHalfSpace = styled(Button)`
  width: 90px;
`;

export enum FilterInputType {
  Text = 'text',
  Date = 'date',
  CheckBox = 'checkBox',
  DateRange = 'dateRange',
  Number = 'number',
  Year = 'year',
  Radio = 'radio',
}

declare global {
  enum FilterInputType {
    Text = 'text',
    Date = 'date',
    CheckBox = 'checkBox',
    DateRange = 'dateRange',
    Number = 'number',
    Year = 'year',
    Radio = 'radio',
    Custom = 'custom',
  }
}

export const withA11yFilterIcon = (label: string) => (filtered: boolean) =>
  (
    <>
      <span className="sr-only">{`Filtra per ${label}`}</span>
      <SearchOutlined aria-hidden="true" />
    </>
  );

/**
 * In @types/react@18.2.22 la definizione di React.Key è stata modificata
 * per includere anche bigint. Probabilmente la definizione di antd che usa
 * React.Key è errata, in attesa di un fix upstream usiamo questa funzione
 * di conversione.
 */
export const reactKeysAsCheckboxValues = (keys: React.Key[]) =>
  keys.map(k =>
    typeof k === 'string' || typeof k === 'number' ? k : k?.toString(),
  );

export function createColumnFilter<T extends object>(
  inputType: FilterInputType,
  params?: { options?: CheckboxOptionType[] },
): Partial<TableColumnProps<T>> {
  return {
    filterInputType: inputType,
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      const selected = reactKeysAsCheckboxValues(selectedKeys);
      return (
        <Container>
          <div>
            {inputType === FilterInputType.CheckBox && (
              <CheckboxGroupStyle
                options={params?.options}
                value={selected}
                onChange={e =>
                  setSelectedKeys(e ? e.map((v: any) => v.toString()) : [])
                }
              />
            )}
            {inputType === FilterInputType.Radio && (
              <RadioGroupStyle
                options={params?.options}
                value={selected[0]}
                onChange={e => {
                  setSelectedKeys(e.target.value ? [e.target.value] : []);
                }}
              />
            )}
            {inputType === FilterInputType.Date && (
              <DatePickerFilterStyle
                marginBottom
                placeholder="DD/MM/YYYY"
                format="DD/MM/YYYY"
                value={
                  selected.length > 0 ? dayjs(selected[0], 'DD/MM/YYYY') : null
                }
                onChange={(e: Dayjs) =>
                  setSelectedKeys(e ? [e.format('DD/MM/YYYY')] : [])
                }
              />
            )}
            {inputType === FilterInputType.Text && (
              <InputStyle
                placeholder={t(
                  'table.table-column-filter.text-placeholder',
                  'Cerca',
                )}
                value={selected[0]}
                onChange={e =>
                  setSelectedKeys(e.target.value ? [e.target.value] : [])
                }
                onPressEnter={() => {
                  confirm({ closeDropdown: true });
                }}
              />
            )}
            {inputType === FilterInputType.Number && (
              <InputStyle
                type="number"
                placeholder={t(
                  'table.table-column-filter.text-placeholder',
                  'Cerca',
                )}
                value={selected[0]}
                onChange={e =>
                  setSelectedKeys(e.target.value ? [e.target.value] : [])
                }
                onPressEnter={() => {
                  confirm({ closeDropdown: true });
                }}
              />
            )}
            {inputType === FilterInputType.Year && (
              <DatePickerFilterStyle
                marginBottom
                picker="year"
                value={
                  selected && selected.length > 0 && selected[0]
                    ? dayjs().year(Number(selected[0]))
                    : undefined
                }
                onChange={e => setSelectedKeys(e ? [e.year()] : [])}
              />
            )}
            {inputType === FilterInputType.DateRange && (
              <Space direction="vertical">
                <DatePickerFilterStyle
                  placeholder={t(
                    'table.table-column-filter.date-range-from-placeholder',
                    'Da: DD/MM/YYYY',
                  )}
                  format="DD/MM/YYYY"
                  value={selected[0] ? dayjs(selected[0], 'YYYY-MM-DD') : null}
                  onChange={e => {
                    const nextSelected = [
                      e?.format('YYYY-MM-DD') ?? null,
                      selected[1],
                    ];
                    setSelectedKeys(nextSelected as React.Key[]);
                  }}
                />
                <DatePickerFilterStyle
                  marginBottom
                  placeholder={t(
                    'table.table-column-filter.date-range-to-placeholder',
                    'A: DD/MM/YYYY',
                  )}
                  format="DD/MM/YYYY"
                  value={selected[1] ? dayjs(selected[1], 'YYYY-MM-DD') : null}
                  onChange={e => {
                    const nextSelected = [
                      selected[0],
                      e?.format('YYYY-MM-DD') ?? null,
                    ];
                    setSelectedKeys(nextSelected as React.Key[]);
                  }}
                />
              </Space>
            )}
          </div>

          {/* PULSANTI */}
          <TableColumnFilterButtons
            clearFilters={clearFilters}
            confirm={confirm}
          />
        </Container>
      );
    },
    filterIcon: filtered => <SearchOutlined />,
  };
}

export interface IDateRangeFilter {
  from?: Date | null;
  to?: Date | null;
}

/**
 * Preso l'array dal filtro di tipo DateRange, restituisce un oggetto con il
 * range di date richiesto.
 * @param array array risultante dal filtro di tipo DateRange su una column.
 * @returns un'oggetto con il range di date nel formato fornito
 */
export function getDateRange(
  array: any[] | undefined | null,
): IDateRangeFilter {
  if (!array || array.length === 0) {
    return {};
  }

  return {
    from:
      array.length > 0 && array[0]
        ? dayjs(array[0], 'YYYY-MM-DD').startOf('day').toDate()
        : null,
    to:
      array.length > 1 && array[1]
        ? dayjs(array[1], 'YYYY-MM-DD').endOf('day').toDate()
        : null,
  };
}

/**
 * Ritorna il valore del filtro richiesto
 * @key chiave del campo del filtro (dataIndex)
 */
export function getFilter(
  filters: Record<string, Array<boolean | React.Key> | null>,
  key: string,
): boolean | React.Key | null {
  return filters[key] ? filters[key]![0] : null;
}

export function formatDateFilter(date: Maybe<Date>) {
  if (date == null) return undefined;
  return dayjs(date).format('YYYY-MM-DD');
}
